// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { spacing } from "@recidiviz/design-system";
import { observer } from "mobx-react-lite";
import { rem } from "polished";
import React from "react";
import styled from "styled-components";

import {
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  DropdownToggle,
  palette,
} from "~design-system";

import Checkbox from "../../components/Checkbox";
import { FilterPresenter } from "../../FilterStore/FilterPresenter";
import useIsMobile from "../../hooks/useIsMobile";
import { FilterField, FilterOption, FilterType } from "../models/types";
import { MobileWorkflowsFilterModal } from "./MobileWorkflowsFilterModal";

const FilterDropdownToggle = styled(DropdownToggle)`
  padding: 12px 16px;
  height: 40px;

  &:hover {
    // Override the default color inversion on hover
    background-color: ${palette.slate10};
    color: ${palette.pine4};
  }
  &:focus {
    background-color: unset;
  }
`;

const FilterIcon = styled.i.attrs({
  className: "fa fa-filter",
})<{ $filters: boolean }>`
  color: ${({ $filters }) => ($filters ? palette.pine4 : palette.slate30)};
  margin-top: -2px;
  padding-right: 4px;
`;

const FilterDownArrow = styled.i.attrs({
  className: "fa fa-caret-down",
})`
  margin-top: -2px;
  padding-left: 8px;
`;

// TODO: Replace the magic numbers with calculations
const FILTER_COLUMN_WIDTH = 250;

const FilterDropdownMenu = styled(DropdownMenu)<{ $alignedWithLeft?: boolean }>`
  ${({ $alignedWithLeft }) =>
    !$alignedWithLeft &&
    `transform: translateX(-${rem(2 * FILTER_COLUMN_WIDTH - 26)}) translateY(4px);`}
  padding: 24px 22px;
`;

export const FilterGroup = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  white-space: nowrap;

  &:not(:first-child) {
    margin-top: ${({ $isMobile }) => ($isMobile ? rem(12) : "18px")};
    padding-top: 18px;
    border-top: ${palette.slate30} 1px solid;
  }

  ${({ $isMobile }) => $isMobile && `gap: ${rem(spacing.xs)};`}
`;

export const FilterGroupHeader = styled.div`
  text-transform: uppercase;
  font-size: ${rem(12)};
  font-weight: 700;
  padding: 0;
  padding-bottom: ${rem(spacing.xs)};
`;

const FilterRightText = `
  border: none;
  padding-right: ${rem(spacing.xs)};
  margin-left: ${rem(spacing.xs)};
  // Reserves a stable footprint for this slot so swapping its contents on
  // hover (a count vs. the "ONLY" button) doesn't change the row's -- and
  // therefore the max-content-sized column's -- total width.
  min-width: ${rem(36)};
  flex-shrink: 0;
  text-align: right;
`;

const FilterCount = styled.div<{ $isZero: boolean }>`
  ${FilterRightText}
  color: ${({ $isZero }) => ($isZero ? palette.slate30 : palette.pine4)};
`;

const SelectOnlyButton = styled.div`
  ${FilterRightText}
  color: ${palette.pine4};
  display: none;
  padding: unset;

  &:hover {
    background-color: ${palette.slate10};
    border-radius: 10px;
  }
`;

const StyledFilterDropdownMenuItem = styled(DropdownMenuItem)<{
  $disabled?: boolean;
}>`
  padding-left: 0;
  padding-right: 0;
  overflow: hidden;
  color: ${palette.pine4};
  // 17px text height plus 4px padding top and bottom
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(spacing.sm)};

  &:last-child {
    margin-bottom: 0;
  }

  ${({ $disabled }) =>
    // If this item is not disabled, show the "only" button and hide the count when hovered
    !$disabled &&
    `
  &:hover {
    color: ${palette.pine1};
    & > ${SelectOnlyButton} {
      display: block;
      color: ${palette.pine1};
    }

    & > ${FilterCount} {
      display: none;
    }
  }`}

  &:active,
  &:focus {
    // Override the default color inversion on hover
    background-color: unset;
    color: ${palette.pine1};
  }

  ${({ $disabled }) => $disabled && `cursor: not-allowed !important;`}
`;

const FilterOptionLabel = styled.div<{ $disabled: boolean }>`
  display: flex;
  justify-content: flex-start;
  gap: 8px;

  ${({ $disabled }) => $disabled && `color: ${palette.slate30};`}
`;

const FilterCheckboxContainer = styled.div`
  & label.Checkbox__container {
    margin-bottom: 0;
    margin-top: 8px;
    padding-left: 0;
  }
`;

const FilterGroupColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 36px;
  border-bottom: ${palette.slate30} 1px solid;
  padding-bottom: 24px;
`;

const FilterGroupColumn = styled.div`
  width: max-content;
  min-width: ${rem(FILTER_COLUMN_WIDTH)};
  max-width: ${rem(FILTER_COLUMN_WIDTH * 1.6)};
`;

const ClearAllButton = styled(StyledFilterDropdownMenuItem)`
  margin-top: ${rem(spacing.md)};
  text-transform: uppercase;
  font-weight: 700;

  &:hover {
    margin-left: unset;
  }
`;

const WorkflowsFilterDropdownItem = observer(
  function WorkflowsFilterDropdownItem({
    option,
    onClick,
    onClickOnly,
    checked,
    count,
  }: {
    option: FilterOption;
    onClick: () => void;
    onClickOnly: () => void;
    checked: boolean;
    count: number;
  }) {
    const disabled = count === 0;
    return (
      <StyledFilterDropdownMenuItem
        preventCloseOnClickEvent
        onClick={(e) => {
          e.preventDefault();
          if (!disabled) onClick();
        }}
        key={String(option.value)}
        $disabled={disabled}
      >
        <FilterOptionLabel $disabled={false}>
          <FilterCheckboxContainer>
            <Checkbox
              checked={checked}
              value={String(option.value)}
              disabled={disabled}
            />
          </FilterCheckboxContainer>
          {option.shortLabel ?? option.label ?? option.value}
        </FilterOptionLabel>
        {<FilterCount $isZero={count === 0}>{count}</FilterCount>}
        <SelectOnlyButton
          onClick={(e) => {
            onClickOnly();
            e.stopPropagation();
          }}
        >
          ONLY
        </SelectOnlyButton>
      </StyledFilterDropdownMenuItem>
    );
  },
);

const WorkflowsFilterDropdownGroup = observer(
  function WorkflowsFilterDropdownGroup({
    type,
    field,
    options,
    presenter,
    title,
  }: {
    type: FilterType;
    field: FilterField;
    options: FilterOption[];
    presenter: FilterPresenter;
    title: string;
  }) {
    return (
      <FilterGroup $isMobile={false}>
        <FilterGroupHeader>{title}</FilterGroupHeader>
        {options.map((option) => (
          <WorkflowsFilterDropdownItem
            key={String(option.value)}
            option={option}
            checked={presenter.filterStore.filterIsSelected(field, option)}
            onClick={() => presenter.filterStore.toggleFilter(field, option)}
            onClickOnly={() =>
              presenter.filterStore.setOnlyFilterForField(field, option)
            }
            count={presenter.numItems(type, field, option)}
          />
        ))}
      </FilterGroup>
    );
  },
);

const ClearAll = observer(function ClearAll({
  presenter,
}: {
  presenter: FilterPresenter;
}) {
  if (presenter.filterStore.allFiltersSelected) {
    return (
      <ClearAllButton
        preventCloseOnClickEvent
        onClick={() => presenter.filterStore.clearFilters()}
      >
        Clear all filters
      </ClearAllButton>
    );
  }

  return (
    <ClearAllButton
      preventCloseOnClickEvent
      onClick={() => presenter.filterStore.selectAllFilters()}
    >
      Select all filters
    </ClearAllButton>
  );
});

const WorkflowsFilterDropdownButton = observer(
  function WorkflowsFilterDropdownButton({
    presenter,
  }: {
    presenter: FilterPresenter;
  }) {
    const numFiltersSet = presenter.filterStore.numFiltersSet;
    const someFiltersSet = numFiltersSet > 0;

    return (
      <>
        <FilterIcon $filters={someFiltersSet} /> Filters
        {someFiltersSet && ` (${numFiltersSet})`}
        <FilterDownArrow />
      </>
    );
  },
);

export const WorkflowsFilterDropdown = observer(
  function WorkflowsFilterDropdown({
    presenter,
    alignedWithLeft = false,
  }: {
    presenter: FilterPresenter;
    alignedWithLeft?: boolean;
  }) {
    const { isMobile } = useIsMobile(true);

    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    React.useEffect(() => {
      if (!isMobile) setModalIsOpen(false);
    }, [isMobile]);

    if (isMobile) {
      return (
        <>
          <Dropdown>
            <FilterDropdownToggle
              // TODO(#7899): Replace with a proper onMenuOpen handler
              onMouseUp={() => {
                presenter.trackFilterDropdownOpened();
                setModalIsOpen(true);
              }}
            >
              <WorkflowsFilterDropdownButton presenter={presenter} />
            </FilterDropdownToggle>
          </Dropdown>

          <MobileWorkflowsFilterModal
            presenter={presenter}
            modalIsOpen={modalIsOpen}
            setModalIsOpen={setModalIsOpen}
          />
        </>
      );
    }

    const filters = presenter.filterStore.filters;

    return (
      <Dropdown>
        <FilterDropdownToggle
          // TODO(#7899): Replace with a proper onMenuOpen handler
          onMouseUp={() => presenter.trackFilterDropdownOpened()}
        >
          <WorkflowsFilterDropdownButton presenter={presenter} />
        </FilterDropdownToggle>
        <FilterDropdownMenu $alignedWithLeft={alignedWithLeft}>
          <FilterGroupColumns>
            <FilterGroupColumn>
              {filters
                .slice(0, Math.ceil(filters.length / 2))
                .map(({ type, field, options, title }) => (
                  <WorkflowsFilterDropdownGroup
                    key={field}
                    type={type}
                    title={title}
                    field={field}
                    options={options}
                    presenter={presenter}
                  />
                ))}
            </FilterGroupColumn>
            <FilterGroupColumn>
              {filters
                .slice(Math.ceil(filters.length / 2))
                .map(({ type, field, options, title }) => (
                  <WorkflowsFilterDropdownGroup
                    key={field}
                    type={type}
                    title={title}
                    field={field}
                    options={options}
                    presenter={presenter}
                  />
                ))}
            </FilterGroupColumn>
          </FilterGroupColumns>
          <ClearAll presenter={presenter} key="clear" />
        </FilterDropdownMenu>
      </Dropdown>
    );
  },
);
